import { useState } from 'react';
import { AccordionDetails, AccordionSummary, Box, Button, Stack, Typography } from '@mui/material';
import { CloudUpload, ExpandMore } from '@mui/icons-material';
import { ModalsKeys, Nullable } from 'global';
import { useModal } from 'hooks/useModal';
import { useInvoice } from 'modules/invoice';
import { UploadedFileView } from 'components/shared/UploadedFileView/UploadedFileView';
import { FlatAccordion } from './FlatAccordion';
import { Document } from '@trnsact/trnsact-shared-types';

interface Props {
  vendorOpportunityId: string;
  dynamicsVendorOpportunityId: string;
  invoiceDoc: Nullable<Document>;
  refetchDocs: VoidFunction;
}

export const DocumentsSection = ({
  vendorOpportunityId,
  dynamicsVendorOpportunityId,
  invoiceDoc,
  refetchDocs,
}: Props) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const { handleOpen } = useModal(ModalsKeys.UploadInvoiceDialog);
  const { isDeleting, handleDeleteInvoiceDocument } = useInvoice();

  const [docType, docName] = invoiceDoc?.docDescription?.split('#') ?? [];

  const openUploadDialog = (file?: File) => {
    handleOpen({ VOId: vendorOpportunityId, dynamicsVOId: dynamicsVendorOpportunityId, file });
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    openUploadDialog(event.dataTransfer.files?.[0]);
  };

  const handleUploadButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    openUploadDialog();
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = invoiceDoc?.source!;
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
  };

  const handleDelete = async () => {
    const success = await handleDeleteInvoiceDocument(invoiceDoc?.documentId!);
    if (success) refetchDocs();
  };

  return (
    <FlatAccordion defaultExpanded disableGutters>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1} pr={1}>
          <Typography fontWeight={500}>Documents</Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={400}>
            {invoiceDoc ? 1 : 0} files
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {invoiceDoc ? (
          <UploadedFileView
            type={docType && docType[0].toUpperCase() + docType.slice(1)}
            name={docName}
            uploadedBy={invoiceDoc.createdBy!}
            uploadedAt={invoiceDoc.createdDateTime}
            onDownload={handleDownload}
            onDelete={handleDelete}
            isDeleting={isDeleting}
          />
        ) : (
          <Box
            onClick={() => openUploadDialog()}
            onDragOver={event => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 3,
              border: '3px dashed',
              borderColor: isDragOver ? 'primary.main' : '#0000001F',
              borderRadius: 1,
              bgcolor: isDragOver ? 'action.hover' : 'transparent',
              cursor: 'pointer',
            }}
          >
            <CloudUpload color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="body2" fontWeight={400}>
              Drag and drop a file here or click to browse
            </Typography>
            <Button variant="outlined" onClick={handleUploadButtonClick}>
              Upload Invoice
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </FlatAccordion>
  );
};
